/**
 * A Manual Control funding bag (`type-id` -> amount) in the client's words: which account, how much.
 * Used wherever a deposit's funding is listed — the Suggestion Engine card's allocation grid and the
 * plan recap — so the second room's deposit is shown from the same assets the first room's is
 * (owner, 7 Oct 2026: "where is the other $310K coming from").
 */
import { parseRadFundingAssetKey, findAssetInFactFind } from '../../utils/entityFundingUtils.ts';
import { assetDisplayName } from '../../utils/assetDisplayName.ts';

const ASSET_TYPE_FALLBACKS: Record<string, string> = {
    bankAccounts: 'bank account',
    termDeposits: 'term deposit',
    accountBasedPensions: 'account-based pension',
    shares: 'shares',
    managedFunds: 'managed funds',
    investmentBonds: 'investment bond',
};

export interface FundingLine { key: string; label: string; amount: number }

export function describeFundingKeys(factFind: unknown, bag: Record<string, number | string> | null | undefined): FundingLine[] {
    return Object.entries(bag || {})
        .map(([key, raw]) => ({ key, amount: Number(raw) || 0 }))
        .filter((e) => e.amount > 0)
        .map(({ key, amount }) => {
            const parsed = parseRadFundingAssetKey(key);
            const located = parsed ? findAssetInFactFind(factFind as never, parsed.assetType, parsed.assetId) : null;
            const fallback = (parsed ? ASSET_TYPE_FALLBACKS[parsed.assetType] : null) || 'savings';
            const label = located ? assetDisplayName(located.asset as never, factFind as never, fallback) : fallback;
            return { key, label, amount };
        });
}

/** Kinds that are SOLD to fund a deposit (a sale, with capital gains tax to consider) as against drawn down. */
const SOLD_KINDS = new Set(['shares', 'managedFunds', 'investmentBonds', 'investmentProperties']);

/**
 * What funding the deposit does to the money: drawn down, or sold. The hub used to say "Nothing is
 * sold" beside $500,000 taken out of a bank account and two pensions (owner, 18 Oct 2026: "we are
 * selling down assets"); now it says which accounts are drawn down and which holdings are sold.
 */
export function fundingNatureSentence(lines: FundingLine[]): string {
    const sold = lines.filter((l) => SOLD_KINDS.has(parseRadFundingAssetKey(l.key)?.assetType || ''));
    const drawn = lines.filter((l) => !sold.includes(l));
    if (!lines.length) return '';
    const names = (xs: FundingLine[]) => xs.map((l) => l.label).join(xs.length > 2 ? ', ' : ' and ');
    if (!sold.length) return `${drawn.length > 1 ? 'These accounts are drawn down' : `${names(drawn)} is drawn down`}; no shares or property are sold.`;
    if (!drawn.length) return `${names(sold)} ${sold.length > 1 ? 'are' : 'is'} sold, with capital gains tax to consider.`;
    return `${names(drawn)} ${drawn.length > 1 ? 'are' : 'is'} drawn down; ${names(sold)} ${sold.length > 1 ? 'are' : 'is'} sold, with capital gains tax to consider.`;
}

/** "$50,000 from anz, $60,000 from MLC and $150,000 from HUB" */
export function fundingPhrase(lines: FundingLine[], formatCurrency: (n: number) => string): string {
    const parts = lines.map((l) => `${formatCurrency(l.amount)} from ${l.label}`);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
